import React, { FC, useMemo, useEffect, useRef, useCallback, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import moment from 'moment';
import { formatSchedulerTime, formatSchedulerTimeRange } from 'shared/utils/dates/formatSchedulerTime';
import { MbscCalendarEvent } from '@mobiscroll/react';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import { Fullscreen, FullscreenExit } from '@mui/icons-material';
import { useQuery } from 'react-query';

import { fetchAllEventTypes } from 'core/API/events';
import { getEnrichedWorkplaceUsers } from 'core/utils/enrichWorkplaceUsers';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { IWorkplaces } from 'module/workplaces/interface/WorkplacesInterface';
import { ScheduleResource, EventTypeResource } from 'types/scheduler';
import { Holiday } from 'types/holidays';
import { useCalendarView } from 'core/hooks/useCalendarView';
import {
	ScheduleEvent,
	ScheduleResource as ScheduleResourceComponent,
} from './ScheduleEvent.component';
import ScheduleEventsHeader from './ScheduleEventsHeader';
import './DesktopPage.style.scss';
import './FullCalendarSchedule.scss';

interface IFullCalendarSchedule {
	openEditShiftModal: (args: any) => void;
	openAddShiftModal: (args: any) => void;
	allEvents: MbscCalendarEvent[];
	holidays?: Holiday[];
	workplacesData: IWorkplaces;
	updateTimeRange: (firstday: Date, lastday: Date) => void;
	setCalendarType: (args: 'schedule' | 'request' | 'event') => void;
	calendarType: string;
	openRequestsModal: (args: any) => void;
	openAddEventModal: (args: any) => void;
	openEditEventModal: (args: any) => void;
	refetch?: any;
	dragTimeStep?: number;
	resourcesOverride?: ScheduleResource[];
	renderHeaderOverride?: () => React.ReactElement | null;
	startOfDay?: string;
	startOfWeek?: string;
	workplaceWorkingHoursStart?: string;
	selectedDate?: Date;
	businessWeekStart?: string;
	onEditShift?: (event: MbscCalendarEvent) => void;
	onDeleteShift?: (event: MbscCalendarEvent) => void;
	onCopyShift?: (event: MbscCalendarEvent) => void;
	onPublish?: () => void;
	canManageShifts?: boolean;
	canDeleteShifts?: boolean;
	isShiftActionsEnabled?: boolean;
	isShiftsLoading?: boolean;
	onShiftDragUpdate?: (event: any) => void;
	onCreateShiftByDrag?: (args: {
		start: Date;
		end: Date;
		resourceId: string;
	}) => void;
}

const FullCalendarSchedule: FC<IFullCalendarSchedule> = ({
	openEditShiftModal: _openEditShiftModal,
	openAddShiftModal,
	allEvents,
	holidays: _holidays = [],
	workplacesData,
	updateTimeRange: _updateTimeRange,
	setCalendarType,
	calendarType,
	openRequestsModal: _openRequestsModal,
	openEditEventModal: _openEditEventModal,
	refetch: _refetch,
	dragTimeStep,
	resourcesOverride,
	renderHeaderOverride,
	startOfDay = '00:00',
	startOfWeek,
	workplaceWorkingHoursStart,
	selectedDate,
	businessWeekStart,
	onEditShift,
	onDeleteShift,
	onCopyShift,
	onPublish,
	canManageShifts = false,
	canDeleteShifts = false,
	isShiftActionsEnabled = true,
	isShiftsLoading = false,
	onShiftDragUpdate,
	onCreateShiftByDrag,
}) => {
	const { workplaceId } = useWorkplacesContext();
	const { calendarView } = useCalendarView();
	const calendarRef = useRef<FullCalendar>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const canInteractWithShifts = canManageShifts && isShiftActionsEnabled;
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [isFullscreenSupported, setIsFullscreenSupported] = useState(false);
	const isWeekLikeView = calendarView === 'week' || calendarView === 'workweek';

	const compactSizing = useMemo(() => {
		if (isWeekLikeView) {
			return {
				resourceAreaWidth: '160px',
				slotMinWidth: 180,
				eventMinWidth: 140,
			};
		}

		return {
			resourceAreaWidth: '170px',
			slotMinWidth: 42,
			eventMinWidth: 32,
		};
	}, [isWeekLikeView]);

	const toggleFullscreen = useCallback(async () => {
		if (!isFullscreenSupported || !containerRef.current) return;

		try {
			if (document.fullscreenElement) {
				await document.exitFullscreen();
				return;
			}

			await containerRef.current.requestFullscreen();
		} catch (_error) {
			setIsFullscreen(false);
		}
	}, [isFullscreenSupported]);

	const { isLoading: isLoadingUsersWorkplace } = useQuery(
		['workplace-employees', workplaceId],
		() => getEnrichedWorkplaceUsers(workplaceId),
		{
			staleTime: 300000,
			enabled: !!workplaceId,
		}
	);

	const { data: eventTypes } = useQuery(['eventTypes'], () => fetchAllEventTypes(), {
		enabled: calendarType === 'event',
	});

	const eventResources = useMemo<EventTypeResource[]>(() => {
		const eventsListResource = eventTypes?.items?.map((eventType: any): EventTypeResource => {
			return {
				id: eventType.id,
				name: eventType.type,
			};
		});

		return eventsListResource ?? [];
	}, [eventTypes]);

	// Convert resources to FullCalendar format
	const fullCalendarResources = useMemo(() => {
		const resources: ScheduleResource[] =
			resourcesOverride ?? (eventResources && eventResources.length ? eventResources : []);

		return resources.map((resource) => ({
			id: String(resource.id),
			title: resource.name,
			extendedProps: {
				...resource,
			},
		}));
	}, [resourcesOverride, eventResources]);

	// Convert events to FullCalendar format
	const fullCalendarEvents = useMemo(() => {
		return allEvents.map((event: any) => {
			const timeRange = formatSchedulerTimeRange(event.start, event.end);

			// For week view, normalize to day boundaries so events occupy full day slot
			let displayStart = event.start;
			let displayEnd = event.end;

			if (calendarView === 'week' || calendarView === 'workweek') {
				// Set to start of day (00:00:00)
				displayStart = moment(event.start).startOf('day').toDate();
				// Set to end of day (23:59:59)
				displayEnd = moment(event.start).endOf('day').toDate();
			}

			return {
				id: event.shift_id || event.id,
				resourceId: String(event.resource),
				title: event.title || '',
				start: displayStart,
				end: displayEnd,
				backgroundColor: event.background_color || event.color,
				borderColor: event.background_color || event.color,
				textColor: event.text_color || '#000',
				editable: canInteractWithShifts && event.editable !== false,
				extendedProps: {
					...event,
					// Ensure startAt and endAt are preserved for ScheduleEvent component
					startAt: event.startAt || event.start,
					endAt: event.endAt || event.end,
					timeRange, // Add formatted time range for display
					actualStart: event.start, // Preserve actual times for operations
					actualEnd: event.end,
				},
			};
		});
	}, [allEvents, canInteractWithShifts, calendarView]);

	// Calculate visible date range based on business week
	const visibleRange = useMemo(() => {
		if (!businessWeekStart) return undefined;

		const start = moment(businessWeekStart).toDate();
		const end = moment(businessWeekStart).add(7, 'days').toDate();

		return { start, end };
	}, [businessWeekStart]);

	// Scroll to selected date only when selectedDate changes (e.g. returning from full edit)
	useEffect(() => {
		if (calendarRef.current && selectedDate) {
			setTimeout(() => {
				// Get the calendar's root element from the container
				const calendarEl = document.querySelector('.fullcalendar-schedule-container .fc') as HTMLElement;
				
				if (!calendarEl) {
					console.log('Calendar element not found');
					return;
				}

				// For timeline views, we need to scroll the timeline body lanes container
				// This is where the actual events are rendered and needs horizontal scrolling
				let timelineElement = calendarEl.querySelector('.fc-timeline-body .fc-timeline-lane-frame') as HTMLElement;
				
				if (!timelineElement) {
					// Try finding the scrollable container for timeline lanes
					timelineElement = calendarEl.querySelector('.fc-timeline-body .fc-scroller') as HTMLElement;
				}
				
				if (!timelineElement) {
					// Try the main timeline scroller
					timelineElement = calendarEl.querySelector('.fc-scroller.fc-scroller-liquid-absolute') as HTMLElement;
				}
				
				// console.log('Scrolling to date:', selectedDate, { 
				// 	found: !!timelineElement,
				// 	businessWeekStart,
				// 	elementClass: timelineElement?.className,
				// 	scrollLeft: timelineElement?.scrollLeft,
				// 	scrollWidth: timelineElement?.scrollWidth,
				// 	clientWidth: timelineElement?.clientWidth,
				// 	parentElement: timelineElement?.parentElement?.className
				// });
				
				if (timelineElement && businessWeekStart) {
					// Calculate the horizontal scroll position based on date AND time
					const weekStart = moment(businessWeekStart);
					const targetDate = moment(selectedDate);
					const daysDiff = targetDate.diff(weekStart, 'days');
					
					// Find the actual scrollable parent if current element isn't scrollable
					let scrollableElement = timelineElement;
					if (timelineElement.scrollWidth <= timelineElement.clientWidth) {
						// Try to find parent that's scrollable
						let parent = timelineElement.parentElement;
						while (parent && parent.scrollWidth <= parent.clientWidth) {
							parent = parent.parentElement;
							if (parent?.classList.contains('fc-scroller')) {
								scrollableElement = parent as HTMLElement;
								break;
							}
						}
						// console.log('Found scrollable parent:', scrollableElement.className, {
						// 	scrollWidth: scrollableElement.scrollWidth,
						// 	clientWidth: scrollableElement.clientWidth
						// });
					}
					
					// Calculate scroll position based on proportion of the week
					// The business week is 7 days, so we calculate the position proportionally
					const totalDaysInWeek = 7;
					const maxScrollLeft = scrollableElement.scrollWidth - scrollableElement.clientWidth;
					
					// Calculate the base scroll position for the day
					const dayScrollPosition = (daysDiff / totalDaysInWeek) * scrollableElement.scrollWidth;
					
					// In day view, also calculate time offset within the day
					// In week view, scroll to start of day only
					let timeOffset = 0;
					let hours = 0;
					let minutes = 0;
					let timeInHours = 0;
					
					if (calendarView === 'day') {
						// Get hours and minutes from the selected date
						hours = targetDate.hours();
						minutes = targetDate.minutes();
						timeInHours = hours + minutes / 60;
						
						// Each day takes up (1/7) of the total scroll width
						const dayWidth = scrollableElement.scrollWidth / totalDaysInWeek;
						
						// Time offset within the day (0-24 hours maps to 0-dayWidth pixels)
						timeOffset = (timeInHours / 24) * dayWidth;
					}
					
					// Combine day position and time offset (timeOffset is 0 in week view)
					const scrollLeft = dayScrollPosition + timeOffset;
					
					// Clamp to valid scroll range
					const clampedScrollLeft = Math.max(0, Math.min(scrollLeft, maxScrollLeft));
					
					// console.log('Scrolling to date and time:', { 
					// 	calendarView,
					// 	daysDiff,
					// 	hours,
					// 	minutes,
					// 	timeInHours,
					// 	totalDaysInWeek,
					// 	scrollWidth: scrollableElement.scrollWidth,
					// 	clientWidth: scrollableElement.clientWidth,
					// 	dayScrollPosition,
					// 	timeOffset,
					// 	maxScrollLeft,
					// 	calculatedScroll: scrollLeft,
					// 	clampedScrollLeft,
					// 	currentScrollLeft: scrollableElement.scrollLeft 
					// });
					
					// Scroll to the exact date and time position
					scrollableElement.scrollLeft = clampedScrollLeft;
				}
			}, 1000);
		}
	}, [selectedDate, businessWeekStart, calendarView]);

	// Change view when calendarView changes
	useEffect(() => {
		if (calendarRef.current) {
			const calendarApi = calendarRef.current.getApi();
			// Both day and week views show the entire business week
			// The difference is in slot duration (configured separately)
			const targetView = 'resourceTimelineWeek';
			if (calendarApi.view.type !== targetView) {
				calendarApi.changeView(targetView);
			}
		}
	}, [calendarView]);

	useEffect(() => {
		setIsFullscreenSupported(Boolean(document.fullscreenEnabled));

		const onFullscreenChange = () => {
			const container = containerRef.current;
			const activeElement = document.fullscreenElement;
			setIsFullscreen(Boolean(container && activeElement && container.contains(activeElement)));
		};

		document.addEventListener('fullscreenchange', onFullscreenChange);
		return () => {
			document.removeEventListener('fullscreenchange', onFullscreenChange);
		};
	}, []);

	useEffect(() => {
		if (!calendarRef.current) return;

		const timer = window.setTimeout(() => {
			calendarRef.current?.getApi().updateSize();
		}, 80);

		return () => window.clearTimeout(timer);
	}, [calendarView, isFullscreen]);

	// Handle event click
	const handleEventClick = useCallback(
		(info: any) => {
			if (!canInteractWithShifts || !onEditShift || calendarType !== 'schedule') return;

			const originalEvent = info.event.extendedProps;
			onEditShift(originalEvent);
		},
		[canInteractWithShifts, onEditShift, calendarType]
	);

	// Handle date click (cell click)
	const handleDateClick = useCallback(
		(info: any) => {
			if (!canInteractWithShifts || calendarType !== 'schedule') return;

			// Drag-to-create is handled via select; single clicks use default shift length
			if (!onCreateShiftByDrag) return;

			const defaultDurationMinutes = dragTimeStep || 15;
			const start = info.date;
			const end = moment(start).add(defaultDurationMinutes, 'minutes').toDate();
			const resourceId = info.resource?.id;

			if (!resourceId) return;

			onCreateShiftByDrag({
				start,
				end,
				resourceId: String(resourceId),
			});
		},
		[canInteractWithShifts, calendarType, onCreateShiftByDrag, dragTimeStep]
	);

	const handleSelect = useCallback(
		(selectInfo: any) => {
			if (!canInteractWithShifts || calendarType !== 'schedule' || !onCreateShiftByDrag) {
				selectInfo.view.calendar.unselect();
				return;
			}

			const resourceId = selectInfo.resource?.id;
			if (!resourceId) {
				selectInfo.view.calendar.unselect();
				return;
			}

			onCreateShiftByDrag({
				start: selectInfo.start,
				end: selectInfo.end,
				resourceId: String(resourceId),
			});

			selectInfo.view.calendar.unselect();
		},
		[canInteractWithShifts, calendarType, onCreateShiftByDrag]
	);

	const handleSelectAllow = useCallback(
		(selectInfo: any) => {
			if (!isWeekLikeView) return true;
			const startDay = moment(selectInfo.start).format('YYYY-MM-DD');
			const endDay = moment(selectInfo.end).subtract(1, 'millisecond').format('YYYY-MM-DD');
			return startDay === endDay;
		},
		[isWeekLikeView]
	);

	const handleEventAllow = useCallback(
		(dropInfo: any, draggedEvent: any) => {
			// Week view: allow moving shifts between employee rows
			if (isWeekLikeView) return true;

			// Day view: block row changes (job code is tied to the row)
			const oldResourceId = draggedEvent.getResources()[0]?.id;
			const newResourceId = dropInfo.resource?.id;
			return oldResourceId === newResourceId;
		},
		[isWeekLikeView]
	);

	// Handle event drop (drag and drop)
	const handleEventDrop = useCallback(
		(info: any) => {
			if (!canInteractWithShifts) {
				info.revert();
				return;
			}

			const oldResourceId = info.oldResource?.id;
			const newResourceId = info.event.getResources()[0]?.id;
			if (
				!isWeekLikeView &&
				oldResourceId &&
				newResourceId &&
				oldResourceId !== newResourceId
			) {
				info.revert();
				return;
			}

			const event = info.event;
			const extendedProps = event.extendedProps;
			const delta = info.delta; // FullCalendar's time delta object

			let newStart: Date;
			let newEnd: Date;

			// For week view, use actual times and apply the day change
			if (calendarView === 'week' || calendarView === 'workweek') {
				// Calculate how many days the event moved
				const daysDiff = delta.days || 0;
				// Apply day difference to actual times (preserves time-of-day)
				newStart = moment(extendedProps.actualStart || extendedProps.startAt || extendedProps.start)
					.add(daysDiff, 'days')
					.toDate();
				newEnd = moment(extendedProps.actualEnd || extendedProps.endAt || extendedProps.end)
					.add(daysDiff, 'days')
					.toDate();
			} else {
				// For day view, use the new times directly from FullCalendar
				newStart = event.start;
				newEnd = event.end;
			}

			// Get the new resource ID (could be employee or jobCode depending on view)
			const droppedResourceId = event.getResources()[0]?.id;

			if (onShiftDragUpdate) {
				const isWeekView = calendarView === 'week' || calendarView === 'workweek';
				const resourceChanged =
					oldResourceId && droppedResourceId && oldResourceId !== droppedResourceId;

				// Pass the complete event with updated times and resource
				onShiftDragUpdate({
					event: {
						...extendedProps,
						originalStartAt: extendedProps.startAt || extendedProps.start, // Preserve original date for metrics
						originalEndAt: extendedProps.endAt || extendedProps.end, // Preserve original date for metrics
						start: newStart,
						end: newEnd,
						startAt: newStart, // Ensure startAt is also updated
						endAt: newEnd, // Ensure endAt is also updated
						resource: droppedResourceId,
						...(isWeekView && resourceChanged
							? {
									employee:
										droppedResourceId === 'open-shifts'
											? null
											: { id: droppedResourceId },
								}
							: {
									employee: extendedProps.employee,
									jobCode: extendedProps.jobCode,
								}),
					},
				});
			}
		},
		[canInteractWithShifts, onShiftDragUpdate, calendarView, isWeekLikeView]
	);

	// Handle event resize
	const handleEventResize = useCallback(
		(info: any) => {
			if (!canInteractWithShifts) {
				info.revert();
				return;
			}

			const event = info.event;
			const extendedProps = event.extendedProps;

			// Resizing only works in day view (disabled in week view via eventDurationEditable)
			// Use the new times directly from FullCalendar
			const newStart = event.start;
			const newEnd = event.end;

			if (onShiftDragUpdate) {
				onShiftDragUpdate({
					event: {
						...extendedProps,
						originalStartAt: extendedProps.startAt || extendedProps.start, // Preserve original date for metrics
						originalEndAt: extendedProps.endAt || extendedProps.end, // Preserve original date for metrics
						start: newStart,
						end: newEnd,
						startAt: newStart, // Ensure startAt is also updated
						endAt: newEnd, // Ensure endAt is also updated
						resource: event.getResources()[0]?.id,
					},
				});
			}
		},
		[canInteractWithShifts, onShiftDragUpdate]
	);

	const isCalendarLoading = isLoadingUsersWorkplace || isShiftsLoading;

	return (
		<div
			ref={containerRef}
			className={`fullcalendar-schedule-container ${isWeekLikeView ? 'is-week-view' : 'is-day-view'} ${isFullscreen ? 'is-fullscreen' : ''}`}
		>
			{renderHeaderOverride ? (
				renderHeaderOverride()
			) : (
				<ScheduleEventsHeader
					workplacesData={workplacesData}
					workplaceId={workplaceId}
					onSwitchCalendarType={setCalendarType}
					onAddShift={() =>
						openAddShiftModal({
							date: selectedDate || new Date(),
							resource: null,
						})
					}
					onPublish={onPublish}
					canManageShifts={canManageShifts}
					isShiftActionsEnabled={isShiftActionsEnabled}
				/>
			)}

			{isFullscreenSupported && (
				<div className="fullcalendar-controls-row">
					<Tooltip
						title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
						placement="left"
						arrow
					>
						<IconButton
							className="calendar-fullscreen-toggle"
							onClick={toggleFullscreen}
							size="small"
							aria-label={isFullscreen ? 'Exit fullscreen calendar' : 'Enter fullscreen calendar'}
						>
							{isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
						</IconButton>
					</Tooltip>
				</div>
			)}

			<div className="fullcalendar-schedule-body">
				{isCalendarLoading && (
					<div className="fullcalendar-schedule-loading">
						<CircularProgress size={40} />
					</div>
				)}

				{!isCalendarLoading && (
			<FullCalendar
				ref={calendarRef}
				plugins={[resourceTimelinePlugin, interactionPlugin, momentTimezonePlugin]}
				schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
				initialView="resourceTimelineWeek"
				headerToolbar={false}
				timeZone="local"
				resources={fullCalendarResources}
				events={fullCalendarEvents}
				editable={canInteractWithShifts && calendarType === 'schedule'}
				eventStartEditable={canInteractWithShifts && calendarType === 'schedule'}
				eventDurationEditable={
					canInteractWithShifts && calendarType === 'schedule' && calendarView === 'day'
				}
				droppable={canInteractWithShifts && calendarType === 'schedule'}
				selectable={canInteractWithShifts && calendarType === 'schedule'}
				selectMirror={true}
				dayMaxEvents={true}
				slotDuration={calendarView === 'day' ? '01:00:00' : '24:00:00'}
				slotLabelInterval={calendarView === 'day' ? '01:00:00' : '24:00:00'}
				{...(calendarView === 'day' && {
					slotLabelContent: (arg: { date: Date; level: number; text: string }) => {
						// Level 0 = date/day row; level 1 = hourly time row
						if (arg.level === 0) {
							return arg.text;
						}
						return formatSchedulerTime(arg.date);
					},
				})}
				slotLabelFormat={
					calendarView === 'day'
						? [
								{ day: 'numeric', weekday: 'short', month: 'long' },
								{
									hour: 'numeric',
									minute: '2-digit',
									omitZeroMinute: false,
									meridiem: 'short',
								},
							]
						: [
								{
									day: 'numeric',
									weekday: 'short',
									month: 'long',
								},
							]
				}
				// dayHeaderFormat={
				// 	calendarView === 'day'
				// 		? { weekday: 'short', month: 'numeric', day: 'numeric', omitCommas: true }
				// 		: { weekday: 'short', month: 'numeric', day: 'numeric', omitCommas: true }
				// }
				// slotLabelContent={
				// 	calendarView === 'day'
				// 		? undefined
				// 		: (arg) => {
				// 				// For week view, show date in slot labels
				// 				const date = moment(arg.date);
				// 				return (
				// 					<div style={{ textAlign: 'center' }}>
				// 						<div style={{ fontWeight: 600, fontSize: '14px' }}>{date.format('ddd')}</div>
				// 						<div style={{ fontSize: '12px' }}>{date.format('M/D')}</div>
				// 					</div>
				// 				);
				// 			}
				// }
				resourceAreaWidth={compactSizing.resourceAreaWidth}
				resourceAreaHeaderContent={calendarView === 'day' ? 'Job Codes' : 'Employees'}
				height="auto"
				contentHeight="auto"
				aspectRatio={2}
				slotMinWidth={compactSizing.slotMinWidth}
				eventMinWidth={compactSizing.eventMinWidth}
				snapDuration={`00:${dragTimeStep || 15}:00`}
				eventOverlap={false}
				displayEventTime={calendarView === 'day'}
				displayEventEnd={calendarView === 'day'}
				slotMinTime={'00:00:00'}
				slotMaxTime="24:00:00"
				scrollTime={workplaceWorkingHoursStart || startOfDay || '08:00:00'}
				firstDay={
					startOfWeek
						? [
								'Sunday',
								'Monday',
								'Tuesday',
								'Wednesday',
								'Thursday',
								'Friday',
								'Saturday',
							].indexOf(startOfWeek)
						: 1
				}
				validRange={visibleRange}
				eventClick={handleEventClick}
				dateClick={handleDateClick}
				select={handleSelect}
				selectAllow={handleSelectAllow}
				eventAllow={handleEventAllow}
				eventDrop={handleEventDrop}
				eventResize={handleEventResize}
				eventContent={(arg) => {
					const event = arg.event.extendedProps;
					return (
						<div className="fc-event-custom">
							<ScheduleEvent
								calendarType={calendarType}
								calendarView={calendarView}
								data={{ original: event } as any}
								onDeleteClick={canDeleteShifts ? onDeleteShift : undefined}
								onCopyClick={canInteractWithShifts ? onCopyShift : undefined}
							/>
						</div>
					);
				}}
				resourceLabelContent={(arg) => {
					const resource = arg.resource.extendedProps;
					return (
						<ScheduleResourceComponent resource={resource as any} calendarType={calendarType} />
					);
				}}
			/>
				)}
			</div>
		</div>
	);
};

export default FullCalendarSchedule;
